import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, Badge, EmptyState } from '../../components/ui';
import { fmt } from '../../utils/format';
import api from '../../utils/api';
import { errMsg } from '../../utils/format';

const STATUS_COLORS = {
  pending:  'warning',
  sent:     'active',
  paid:     'ok',
  overdue:  'critical',
  partial:  'warning',
};

export default function Invoices() {
  const { data, loading, error, refetch } = useFetch('/api/b2b/orders');
  const [markingPaid, setMarkingPaid] = useState(null);

  const [loadingPdf, setLoadingPdf] = useState(null);

  const viewPdf = async (id, invoiceNumber) => {
    setLoadingPdf(id);
    try {
      const response = await api.get(`/api/b2b/orders/${id}/invoice-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { alert('Could not load PDF: ' + errMsg(err)); }
    finally { setLoadingPdf(null); }
  };

  const markPaid = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Mark this invoice as paid?')) return;
    setMarkingPaid(id);
    try {
      await api.patch(`/api/b2b/orders/${id}`, { invoiceStatus: 'paid' });
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setMarkingPaid(null); }
  };

  const orders = (data || []).filter(o => o.invoice_number);
  const now = new Date();

  // Mark overdue automatically in display
  const enriched = orders.map(o => {
    let status = o.invoice_status;
    if (status === 'sent' && o.due_date && new Date(o.due_date) < now) {
      status = 'overdue';
    }
    return { ...o, display_status: status };
  });

  const totalOutstanding = enriched
    .filter(o => ['sent','overdue'].includes(o.display_status))
    .reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="page-header">
        <h1>Invoices</h1>
        <div className="page-actions">
          <button className="btn btn-sm" onClick={refetch}><i className="ti ti-refresh" /> Refresh</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 }}>
        {[
          { label:'Total invoices', value: enriched.length, color: 'var(--text-primary)' },
          { label:'Outstanding', value: enriched.filter(o => ['sent','overdue'].includes(o.display_status)).length, color: 'var(--amber)' },
          { label:'Overdue', value: enriched.filter(o => o.display_status === 'overdue').length, color: 'var(--red)' },
          { label:'Outstanding amount', value: fmt.currency(totalOutstanding), color: 'var(--blue)' },
        ].map((card, i) => (
          <div key={i} className="card" style={{ padding:'12px 16px' }}>
            <div style={{ fontSize:22, fontWeight:600, color:card.color }}>{card.value}</div>
            <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:2 }}>{card.label}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>PO Number</th>
                <th>Customer</th>
                <th>Shopify Order</th>
                <th>Amount</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enriched.map(o => (
                <tr key={o.id} style={{
                  background: o.display_status === 'overdue' ? 'var(--red-light, #fff5f5)' : undefined
                }}>
                  <td style={{ fontFamily:'monospace', fontWeight:600, fontSize:12 }}>{o.invoice_number}</td>
                  <td style={{ fontFamily:'monospace', fontSize:12 }}>{o.po_number}</td>
                  <td style={{ fontSize:13 }}>{o.parsed_data?.bill_to?.company || o.vendor_name || o.raw_email_from || '—'}</td>
                  <td style={{ fontFamily:'monospace', fontSize:12 }}>{o.shopify_order_name || '—'}</td>
                  <td style={{ fontWeight:600 }}>{o.total_amount ? fmt.currency(o.total_amount) : '—'}</td>
                  <td style={{ fontSize:12 }}>{o.invoice_sent_at ? new Date(o.invoice_sent_at).toLocaleDateString() : '—'}</td>
                  <td style={{
                    fontSize:12,
                    fontWeight: o.display_status === 'overdue' ? 600 : 'normal',
                    color: o.display_status === 'overdue' ? 'var(--red)' : undefined,
                  }}>
                    {o.due_date ? new Date(o.due_date).toLocaleDateString() : '—'}
                  </td>
                  <td><Badge status={STATUS_COLORS[o.display_status]} label={o.display_status} /></td>
                  <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <button className="btn btn-sm" disabled={loadingPdf === o.id} onClick={() => viewPdf(o.id, o.invoice_number)}>
                      {loadingPdf === o.id ? '…' : <><i className="ti ti-file-type-pdf" /> PDF</>}
                    </button>
                    {['sent','overdue'].includes(o.display_status) && (
                      <button className="btn btn-sm" disabled={markingPaid === o.id}
                        onClick={e => markPaid(o.id, e)}>
                        {markingPaid === o.id ? '…' : <><i className="ti ti-check" /> Mark paid</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!enriched.length && <EmptyState icon="ti-file-invoice" message="No invoices yet" />}
        </div>
      </div>
    </div>
  );
}
